import { typeStyleFor } from "./buildingStyle";
import { applyPanel } from "./UiPanel";
import { PARCHMENT, LAYERS } from "./theme";
import { playUiClick } from "../audio/sfx";
import { createModal } from "./Modal";
// Resets native <button> chrome (border/background/font/alignment) so a row/card button
// reads identically to the plain divs it replaces — only real semantics/focusability change.
const BUTTON_RESET = `
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  font: inherit;
  color: inherit;
  margin: 0;
`;
let dom = null;
function getDom() {
    if (dom)
        return dom;
    const overlay = document.createElement("div");
    overlay.style.cssText = `
    position: fixed;
    inset: 0;
    z-index: ${LAYERS.userBuildingsBackdrop};
    background: rgba(0,0,0,0.55);
    display: none;
  `;
    overlay.addEventListener("click", () => hideUserBuildingsPanel());
    // The panel itself never scrolls — its max-height just caps the box, and applyPanel's
    // nine-patch background is sized to that whole box. Only `body` (below) scrolls, so the
    // header always renders directly on the panel's own background instead of needing an
    // opaque sticky-header color that would paper over the panel's border artwork.
    const panel = document.createElement("div");
    panel.style.cssText = `
    position: fixed;
    z-index: ${LAYERS.userBuildingsPanel};
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 360px;
    max-height: 80vh;
    flex-direction: column;
    font-family: ui-sans-serif, system-ui, sans-serif;
    box-shadow: 0 16px 56px rgba(0,0,0,0.55);
    display: none;
  `;
    applyPanel(panel, "beigeLight");
    const header = document.createElement("div");
    header.style.cssText = `
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px 9px;
    border-bottom: 1px solid ${PARCHMENT.divider};
    flex-shrink: 0;
  `;
    const title = document.createElement("span");
    title.style.cssText = `
    font-size: 12px;
    font-weight: 700;
    color: ${PARCHMENT.text};
  `;
    const closeBtn = document.createElement("button");
    closeBtn.style.cssText = `
    background: none;
    border: none;
    color: ${PARCHMENT.textMuted};
    font-size: 20px;
    line-height: 1;
    padding: 0 2px;
  `;
    closeBtn.textContent = "×";
    closeBtn.addEventListener("click", () => {
        playUiClick();
        hideUserBuildingsPanel();
    });
    header.append(title, closeBtn);
    const body = document.createElement("div");
    body.style.cssText =
        "padding: 4px 0 8px; overflow-y: auto; flex: 1; min-height: 0;";
    panel.append(header, body);
    document.body.appendChild(overlay);
    document.body.appendChild(panel);
    const modal = createModal({ panel, labelledBy: title });
    dom = { overlay, panel, title, body, modal };
    return dom;
}
function renderEmpty(container) {
    const el = document.createElement("div");
    el.style.cssText = `font-size: 11px; color: ${PARCHMENT.textMuted}; padding: 2px 14px 10px;`;
    el.textContent = "Keine Gebäude in dieser Kategorie.";
    container.appendChild(el);
}
// Compact one-line row for city/special buildings — just the type label, no per-item art.
function renderCityRow(container, building, onFocus) {
    const style = typeStyleFor("special", undefined, building.specialType);
    const row = document.createElement("button");
    row.type = "button";
    row.style.cssText = `
    ${BUTTON_RESET}
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border-bottom: 1px solid ${PARCHMENT.divider};
  `;
    const dot = document.createElement("span");
    dot.style.cssText = `
    width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
    background: ${style.color};
  `;
    const name = document.createElement("span");
    name.style.cssText = `font-size: 12px; font-weight: 600; color: ${PARCHMENT.text};`;
    name.textContent = style.label;
    row.append(dot, name);
    row.addEventListener("click", () => {
        playUiClick();
        hideUserBuildingsPanel();
        onFocus(building.id);
    });
    container.appendChild(row);
}
// Rich card for company buildings — title + colored type badge + description, matching
// BuildMenu.ts's card layout minus a sprite preview (no per-company sprite exists yet).
function renderCompanyCard(container, building, onFocus) {
    const style = typeStyleFor("company", building.companyType);
    const card = document.createElement("button");
    card.type = "button";
    card.style.cssText = `
    ${BUTTON_RESET}
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 14px;
    border-bottom: 1px solid ${PARCHMENT.divider};
  `;
    const nameRow = document.createElement("div");
    nameRow.style.cssText = "display: flex; align-items: center; gap: 6px;";
    const name = document.createElement("span");
    name.style.cssText = `font-size: 13px; font-weight: 600; color: ${PARCHMENT.text};`;
    name.textContent = building.title;
    const badge = document.createElement("span");
    badge.style.cssText = `
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.04em;
    padding: 1px 5px;
    border-radius: 3px;
    color: #fff;
    background: ${style.color};
  `;
    badge.textContent = style.label;
    nameRow.append(name, badge);
    const desc = document.createElement("div");
    desc.style.cssText = `font-size: 11px; color: ${PARCHMENT.textMuted}; line-height: 1.35;`;
    desc.textContent = building.description;
    desc.style.display = building.description ? "" : "none";
    card.append(nameRow, desc);
    card.addEventListener("click", () => {
        playUiClick();
        hideUserBuildingsPanel();
        onFocus(building.id);
    });
    container.appendChild(card);
}
// Wraps one category in its own block with a heading, so sections read as clearly separate
// groups rather than a single run-on list — a divider + margin on every section but the first.
function renderSection(body, heading, items, renderItem, onFocus, isFirst) {
    const section = document.createElement("div");
    section.style.cssText = isFirst
        ? ""
        : `margin-top: 8px; padding-top: 8px; border-top: 1px solid ${PARCHMENT.divider};`;
    const heading_ = document.createElement("div");
    heading_.style.cssText = `
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${PARCHMENT.textMuted};
    padding: 2px 14px 6px;
  `;
    heading_.textContent = `${heading} (${items.length})`;
    section.appendChild(heading_);
    if (items.length === 0)
        renderEmpty(section);
    else
        for (const b of items)
            renderItem(section, b, onFocus);
    body.appendChild(section);
}
export function showUserBuildingsPanel(userName, cityBuildings, companyBuildings, onFocus) {
    const d = getDom();
    d.title.textContent = `Gebäude von ${userName}`;
    d.body.textContent = "";
    renderSection(d.body, "Stadt-Gebäude", cityBuildings, renderCityRow, onFocus, true);
    renderSection(d.body, "Unternehmen", companyBuildings, renderCompanyCard, onFocus, false);
    d.overlay.style.display = "block";
    d.panel.style.display = "flex";
    d.modal.open(hideUserBuildingsPanel);
}
export function hideUserBuildingsPanel() {
    if (dom) {
        dom.panel.style.display = "none";
        dom.overlay.style.display = "none";
        dom.modal.close();
    }
}
